import { useRouter } from "next/router";

import FileUploadField from "@/components/shared/formFields/fileUploader";
import Button from "@/components/shared/ui/Button";
import Loader from "@/components/shared/ui/Loader";
import {
	ACCEPTED_FILE_TYPES_FOR_PROJECT_FILES,
	ACCEPTED_FILE_TYPES_FOR_PUBLIC_COMMENT_FILES,
	ACCEPT_ATTRIBUTE_FOR_PUBLIC_COMMENT_FILES,
	MAX_FILE_SIZE,
} from "@/constants";
import useDeletePublicCommentFile from "@/hooks/methodology/useDeletePublicCommentFile";
import useGetAdditionalInfo from "@/hooks/methodology/useGetAdditionalInfo";
import usePostAdditionalInfo from "@/hooks/methodology/usePostAdditionalInfo";
import { MethodologyWithRoundDetails } from "@/types/Methodology";
import { downloadFile } from "@/utils/downloadFile";
import { getFileSize } from "@/utils/getFileSize";
import { getFormattedFileTypeErrorMessage } from "@/utils/validationMessages";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { BsArrowDown } from "react-icons/bs";
import { FiTrash2 } from "react-icons/fi";
import z, { ZodEffects, ZodIssueCode } from "zod";

export const publicCommentResolver = z.object({
	publicCommentFile: z.any().superRefine((val, ctx) => {
		if (!val) {
			ctx.addIssue({
				code: ZodIssueCode.custom,
				message: "File is required",
			});
		} else if (typeof val !== "string") {
			if (val.size > MAX_FILE_SIZE) {
				ctx.addIssue({
					code: ZodIssueCode.custom,
					message: `Max size is ${getFileSize(MAX_FILE_SIZE)}`,
				});
			}
			if (!ACCEPTED_FILE_TYPES_FOR_PROJECT_FILES.includes(val?.type)) {
				ctx.addIssue({
					code: ZodIssueCode.custom,
					message: getFormattedFileTypeErrorMessage(ACCEPT_ATTRIBUTE_FOR_PUBLIC_COMMENT_FILES),
				});
			}
		}
	}) as ZodEffects<z.ZodAny, any, any>,
	existingPublicCommentFile: z
		.object({
			fileUrl: z.string().optional(),
			fileName: z.string().optional(),
			fileSize: z.number().optional(),
		})
		.optional(),
});
type PublicComment = z.infer<typeof publicCommentResolver>;
type PublicCommentFormData = PublicComment & { methodologyAdditionalInfoId?: number };

interface PublicCommentTabProps {
	onSubmitSuccessFn: () => void;
	isOriginator?: boolean;
	roundStatus?: string;
	roundDetails?: MethodologyWithRoundDetails;
}

const PublicComment = (props: PublicCommentTabProps) => {
	const { onSubmitSuccessFn, roundDetails } = props;
	const router = useRouter();
	const { query } = router;
	const params = query;
	const { data, isLoading } = useGetAdditionalInfo(params.id?.toString());
	const { mutate: submitAdditionalInfo, isLoading: isSubmitting } = usePostAdditionalInfo(
		"Commentary and Response file",
		onSubmitSuccessFn,
	);
	const { mutate: deletePublicCommentFile, isLoading: isFileDeleting } = useDeletePublicCommentFile();

	const {
		register,
		handleSubmit,
		watch,
		setValue,
		setError,
		reset,
		formState: { errors },
		trigger,
	} = useForm<PublicCommentFormData>({
		resolver: zodResolver(publicCommentResolver),
		mode: "onBlur",
		defaultValues: {
			publicCommentFile: "",
			methodologyAdditionalInfoId: undefined,
			existingPublicCommentFile: undefined,
		},
	});

	const [backUpData, setBackUpData] = useState<PublicCommentFormData>();
	useEffect(() => {
		if (data?.methodologyAdditionalInfo) {
			const newData: PublicCommentFormData = {
				publicCommentFile: "",
				methodologyAdditionalInfoId: data?.methodologyAdditionalInfo?.id,
			};
			if (data.methodologyAdditionalInfo.additionalInfoFile) {
				newData.existingPublicCommentFile = {
					fileUrl: data.methodologyAdditionalInfo.additionalInfoFile,
					fileName: data.methodologyAdditionalInfo.fileName,
					fileSize: parseInt(data.methodologyAdditionalInfo.fileSize),
				};
			} else {
				newData.existingPublicCommentFile = undefined;
			}

			reset({
				...newData,
				existingPublicCommentFile: newData.existingPublicCommentFile && { ...newData.existingPublicCommentFile },
			});
			setBackUpData({
				...newData,
				existingPublicCommentFile: newData.existingPublicCommentFile && { ...newData.existingPublicCommentFile },
			});
		}
	}, [data]);
	const canEditData =
		data?.status === "CONDITIONALLY_APPROVED" &&
		props.isOriginator &&
		!(data?.methodologyAdditionalInfo?.status === "SUBMITTED");

	const deleteExistingPublicCommentFile = () => {
		// reset({
		// 	existingPublicCommentFile: undefined,
		// 	methodologyAdditionalInfoId: watch("methodologyAdditionalInfoId"),
		// 	publicCommentFile: watch("publicCommentFile"),
		// });
		if (data?.id && data?.methodologyAdditionalInfo?.id) {
			deletePublicCommentFile({
				methodologyId: data?.id,
				additionalInfoId: data?.methodologyAdditionalInfo?.id,
			});
		}
	};

	// For debugging purpose
	const onErrorFn = () => {};

	const watcher = watch("publicCommentFile");
	const existingFilewatcher = watch("existingPublicCommentFile");

	const handleCancel = () => {
		reset({ ...backUpData });
	};

	const onSubmit = () => {
		const formattedData = formatData();
		submitAdditionalInfo(formattedData);
	};

	const formatData = () => {
		const formData: any = new FormData();
		const { publicCommentFile, methodologyAdditionalInfoId } = watch();
		if (methodologyAdditionalInfoId) {
			formData.append("methodologyAdditionalInfoId", methodologyAdditionalInfoId);
		}
		if (publicCommentFile && publicCommentFile !== "string") {
			formData.append("methodologyAdditionalInfoFile", publicCommentFile);
		}
		formData.append("methodologyId", params.id?.toString());
		return formData;
	};

	const handleDownloadFile = (fileUrl: string) => {
		downloadFile(fileUrl);
	};

	if (isLoading) return <Loader />;
	return (
		<div className="space-y-8">
			<>
				{!existingFilewatcher && !canEditData ? (
					<></>
				) : (
					<div className="w-full space-y-8 text-sm md:w-1/2 lg:w-1/2">
						{roundDetails && roundDetails.status !== "APPROVED" && (
							<>
								<span className="text-2xl font-bold">Upload Commentary and Response</span>
								<p>
									You will receive separate instructions on public commentary via email. Once the public commentary
									period is complete and you have finished responding to all comments, please upload the final file
									here.
								</p>
							</>
						)}
					</div>
				)}
			</>

			<div className="w-full text-sm md:w-4/5 lg:w-1/2">
				{existingFilewatcher && (
					<div className="flex w-full items-start justify-between border-[1px] bg-white p-5">
						<div className="flex flex-col space-y-4">
							<h3>Public Commentary File</h3>
						</div>

						<div className="flex items-center gap-x-2">
							<button
								type="button"
								onClick={() => {
									handleDownloadFile(existingFilewatcher.fileUrl!!);
								}}
								className="rounded-full border-[1px] border-gray-500 p-[2px]"
							>
								<BsArrowDown className="text-xs text-gray-600" />
							</button>
							{canEditData &&
								(isFileDeleting ? (
									<div
										className="h-3 w-3 animate-spin rounded-full
							border-2 border-solid border-clearSky-500 border-t-transparent"
									></div>
								) : (
									<button
										type="button"
										className="rounded-full border-[1px] border-gray-500 p-[2px]"
										disabled={isFileDeleting}
										onClick={() => deleteExistingPublicCommentFile()}
									>
										<FiTrash2 className="text-xs text-gray-500" />
									</button>
								))}
						</div>
					</div>
				)}
				{!existingFilewatcher && canEditData && (
					<FileUploadField
						{...register("publicCommentFile")}
						registerName="publicCommentFile"
						errorMessage={
							typeof errors.publicCommentFile?.message === "string" ? errors.publicCommentFile?.message : ""
						}
						label=""
						setValue={setValue}
						setError={setError}
						isFileNull={Boolean(watcher)}
						uniqueId={"publicCommentFile"}
						acceptAttribute={ACCEPT_ATTRIBUTE_FOR_PUBLIC_COMMENT_FILES}
						acceptedTypes={ACCEPTED_FILE_TYPES_FOR_PUBLIC_COMMENT_FILES}
						trigger={trigger}
					/>
				)}
				{!existingFilewatcher && !canEditData && <span className="text-base font-medium">No file available.</span>}
			</div>

			{/* For Tradewater's methodology (id = 8) , we needed to manually show some static text.
			This text won't be visible under any other mtd
			Ref : https://oneshot-earth.atlassian.net/browse/ONESHOT-1792
			*/}
			{!canEditData && data?.id == 8 && (
				<div className="w-full space-y-4 text-sm md:w-4/5 lg:w-2/3">
					<h3>Comment on Reversal Risk, by Thomas Merriman from kita.earth:</h3>
					<p>
						<span className="text-xl">“ </span>The practice of capping hydrocarbon wells is well-established and widely
						understood within the oil and gas industry. Among the various categories of wells that undergo capping,
						abandoned and orphaned wells typically present relatively low risk. This is primarily due to the
						significantly reduced pressure levels commonly observed in these wells, despite the potential deterioration
						of surface infrastructure such as wellheads and pipework.
					</p>
					<p>
						Given this context, the probability of a properly capped abandoned well leaking again appears to be low.
						Should a leak occur, it would most likely stem from poor workmanship or the use of defective or
						inappropriate materials and equipment. However, such risks are already mitigated by existing industry
						standards governing well capping procedures.
					</p>
					<p>
						Furthermore, any issues arising from substandard capping practices would likely become evident within the
						first 18 months following the intervention. This timeframe provides a reasonable window for monitoring and
						verification, which could be incorporated into the design of a robust carbon crediting framework.
					</p>
					<p>
						In summary, the relatively low risk profile of abandoned wells, combined with established industry practices
						and predictable failure timelines, supports the inclusion of well capping activities within a carbon
						crediting standard—provided that appropriate safeguards and verification mechanisms are in place.
						<span className="text-xl"> ”</span>
					</p>
				</div>
			)}

			{canEditData && (
				<div className="flex gap-2 align-middle">
					<Button onClick={handleCancel} variant="outline">
						Cancel
					</Button>
					<Button
						type="submit"
						variant={isSubmitting ? "ghost" : "solid"}
						className="text-black"
						onClick={handleSubmit(onSubmit, onErrorFn)}
						disabled={isSubmitting}
					>
						Save changes
					</Button>
				</div>
			)}
		</div>
	);
};

export default PublicComment;
